import React, { useEffect, useState } from 'react';
import { ImageBlock, isSafeHttpsUrl, VideoBlock } from './MediaBlocks';
import { lessonResourcePreviewUrl, lessonResourceSourceLabel, lessonResourceSourceType } from '../../utils/lessonResources';

export type LessonResource = {
  resource_key?: string;
  title: string;
  resource_type: 'image' | 'video' | 'document' | 'link';
  src: string;
  audience?: 'teacher' | 'student' | 'both';
  needs_printing?: boolean;
  quantity_rule?: 'none' | 'one' | 'per_student' | 'per_group' | 'per_bench' | 'fixed';
  fixed_quantity?: number;
  paper_decision?: 'paper_required' | 'optional_paper_backup' | 'digital_projected_only' | 'teacher_print_backup_required' | 'not_current';
  alt?: string;
  caption?: string;
  description?: string;
  availability: 'step' | 'lesson' | 'unit';
  available_during_apply?: boolean;
  thumbnail?: string;
  source_file_id?: string;
  source_mime_type?: string;
  preview_url?: string;
  access_scope?: 'anyone' | 'organization' | 'restricted' | 'unknown';
  expected_access_scope?: 'anyone' | 'organization' | 'restricted' | 'unknown';
  access_checked_at?: string;
  presentation_mode?: 'entire_deck' | 'selected_slides';
  selected_slides?: Array<{
    slide_number: number;
    object_id: string;
    title?: string;
    thumbnail_url: string;
  }>;
  presentation_synced_at?: string;
};

export const ResourceToolkit: React.FC<{
  resources?: LessonResource[];
  lifecycleKey: string;
  sessionKey?: string;
  preserveVideoPosition?: boolean;
  onResourceOpen?: (resource: LessonResource) => void;
}> = ({ resources = [], lifecycleKey, sessionKey, preserveVideoPosition = false, onResourceOpen }) => {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<LessonResource | null>(null);
  const [selectedSlideIndex, setSelectedSlideIndex] = useState(0);
  const selectedAccessIsStale = Boolean(
    selected?.access_checked_at
    && Date.now() - Date.parse(selected.access_checked_at) > 7 * 24 * 60 * 60 * 1000,
  );

  useEffect(() => {
    setOpen(false);
    setSelected(null);
    setSelectedSlideIndex(0);
  }, [lifecycleKey]);

  if (!resources.length) return null;
  return (
    <section>
      <button
        type="button"
        className="rounded-lg border border-indigo-300 bg-white px-4 py-2 text-sm font-semibold text-indigo-800 shadow-sm focus-visible:ring-2 focus-visible:ring-indigo-600"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
      >
        Resources ({resources.length})
      </button>
      {open ? (
        <div className="mt-3 rounded-xl border border-indigo-200 bg-indigo-50 p-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-indigo-700">Resources / Toolkit</p>
              <p className="mt-1 text-sm text-indigo-950">References open here without changing your lesson step.</p>
            </div>
            <button type="button" className="rounded px-2 py-1 text-sm text-indigo-800 focus-visible:ring-2 focus-visible:ring-indigo-600" onClick={() => { setOpen(false); setSelected(null); }}>Close</button>
          </div>
          <div className="mt-3 grid gap-2 sm:grid-cols-2">
            {resources.map((resource, index) => (
              <button
                key={`${resource.src}:${index}`}
                type="button"
                className="rounded-lg border border-indigo-200 bg-white p-3 text-left focus-visible:ring-2 focus-visible:ring-indigo-600"
                onClick={() => {
                  setSelected(resource);
                  setSelectedSlideIndex(0);
                  onResourceOpen?.(resource);
                }}
              >
                <span className="block font-medium text-slate-900">{resource.title}</span>
                <span className="mt-1 block text-xs text-slate-500">{resource.availability} · {lessonResourceSourceLabel(lessonResourceSourceType(resource as any))}</span>
              </button>
            ))}
          </div>
          {selected ? (
            <div className="mt-4 rounded-xl border border-indigo-200 bg-white p-4">
              <div className="mb-3 flex items-start justify-between gap-3">
                <div>
                  <h3 className="font-semibold text-slate-900">{selected.title}</h3>
                  {selected.description ? <p className="mt-1 text-sm text-slate-600">{selected.description}</p> : null}
                </div>
                <button type="button" className="text-sm text-indigo-700 underline" onClick={() => setSelected(null)}>Close resource</button>
              </div>
              {selected.access_scope === 'restricted' ? (
                <p className="mb-3 rounded border border-rose-300 bg-rose-50 px-3 py-2 text-sm font-medium text-rose-900">
                  Student access warning: this Google Drive file had restricted sharing when it was added. Students may be unable to open it until the teacher updates sharing.
                </p>
              ) : null}
              {selectedAccessIsStale ? (
                <p className="mb-3 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                  Google Drive access was last checked more than 7 days ago and may have changed.
                </p>
              ) : null}
              {selected.presentation_mode === 'selected_slides' && selected.selected_slides?.length ? (
                <div className="space-y-3">
                  <div className="overflow-hidden rounded-lg border border-slate-200 bg-slate-950">
                    <img
                      src={selected.selected_slides[selectedSlideIndex].thumbnail_url}
                      alt={`${selected.title}, slide ${selected.selected_slides[selectedSlideIndex].slide_number}`}
                      className="mx-auto max-h-[65vh] w-full object-contain"
                    />
                  </div>
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <button
                      type="button"
                      className="rounded border border-slate-300 px-3 py-2 text-sm font-medium text-slate-800 disabled:opacity-40"
                      disabled={selectedSlideIndex === 0}
                      onClick={() => setSelectedSlideIndex((value) => Math.max(0, value - 1))}
                    >
                      Previous slide
                    </button>
                    <span className="text-sm text-slate-600">
                      Selected slide {selectedSlideIndex + 1} of {selected.selected_slides.length} · original slide {selected.selected_slides[selectedSlideIndex].slide_number}
                    </span>
                    <button
                      type="button"
                      className="rounded border border-slate-300 px-3 py-2 text-sm font-medium text-slate-800 disabled:opacity-40"
                      disabled={selectedSlideIndex === selected.selected_slides.length - 1}
                      onClick={() => setSelectedSlideIndex((value) => Math.min(selected.selected_slides!.length - 1, value + 1))}
                    >
                      Next slide
                    </button>
                  </div>
                  <a className="inline-flex font-medium text-blue-700 underline focus-visible:ring-2 focus-visible:ring-blue-600" href={selected.src} target="_blank" rel="noreferrer">
                    Open the complete deck
                  </a>
                </div>
              ) : selected.source_file_id && lessonResourcePreviewUrl(selected as any) ? (
                <div className="space-y-2">
                  <iframe
                    src={lessonResourcePreviewUrl(selected as any) || undefined}
                    title={selected.title}
                    className="h-[65vh] min-h-80 w-full rounded-lg border border-slate-200 bg-slate-50"
                    sandbox="allow-scripts allow-same-origin allow-popups"
                    referrerPolicy="no-referrer"
                    allow="fullscreen"
                  />
                  <a className="inline-flex font-medium text-blue-700 underline focus-visible:ring-2 focus-visible:ring-blue-600" href={selected.src} target="_blank" rel="noreferrer">
                    Open in Google Drive
                  </a>
                </div>
              ) : selected.resource_type === 'image' ? (
                <ImageBlock block={{ type: 'image', src: selected.src, alt: selected.alt || selected.title, caption: selected.caption, allow_zoom: true }} />
              ) : selected.resource_type === 'video' ? (
                <VideoBlock
                  block={{ type: 'video', src: selected.src, caption: selected.caption, allow_speed_control: true }}
                  sessionKey={`${sessionKey || 'resource'}:${selected.src}`}
                  preservePosition={preserveVideoPosition}
                />
              ) : selected.resource_type === 'document' && isSafeHttpsUrl(selected.src) ? (
                <div className="space-y-2">
                  <iframe
                    src={selected.src}
                    title={selected.title}
                    className="h-[65vh] min-h-80 w-full rounded-lg border border-slate-200 bg-slate-50"
                    sandbox=""
                    referrerPolicy="no-referrer"
                  />
                  <a className="inline-flex font-medium text-blue-700 underline focus-visible:ring-2 focus-visible:ring-blue-600" href={selected.src} target="_blank" rel="noreferrer">
                    Open document in a new tab if the preview is unavailable
                  </a>
                </div>
              ) : isSafeHttpsUrl(selected.src) ? (
                <a className="inline-flex font-medium text-blue-700 underline focus-visible:ring-2 focus-visible:ring-blue-600" href={selected.src} target="_blank" rel="noreferrer">
                  Open resource in a new tab
                </a>
              ) : <p className="text-sm text-amber-800">This resource URL is unavailable.</p>}
            </div>
          ) : null}
        </div>
      ) : null}
    </section>
  );
};
